import { create } from 'zustand';
import { ZipDownload } from '../services/download/zipDownloader';
import type { DownloadProgress, DownloadScope, DownloadTarget } from '../services/download/types';

/**
 * Holds the one in-flight client-side ZIP download.
 *
 * It lives in a store rather than in the page that started it so the download
 * survives navigation — a guest can keep browsing the album, or leave for
 * another page, while their 8 GB archive keeps writing.
 */

interface DownloadState {
  progress: DownloadProgress | null;
  /** The running download; null once it settles. Drives "is it still going?". */
  active: ZipDownload | null;
  /**
   * The most recent download, kept alive past completion so the finished card
   * can still re-open its archives. Cleared on dismiss, which is also what
   * releases a Blob sink's bytes.
   */
  handle: ZipDownload | null;
  /** Kept past completion so "Retry failed" still knows what to re-run. */
  target: DownloadTarget | null;
  minimized: boolean;
  start: (opts: {
    target: DownloadTarget;
    albumName: string;
    scope: DownloadScope;
    photoIds?: string[];
    confirmLarge?: (bytes: number, partCount: number) => Promise<boolean>;
  }) => Promise<void>;
  cancel: () => void;
  savePart: (partIndex: number) => Promise<void>;
  openPart: (partIndex: number) => void;
  /** Save every pending part into one chosen folder. */
  saveAll: () => Promise<boolean>;
  retrySkipped: () => Promise<void>;
  dismiss: () => void;
  setMinimized: (v: boolean) => void;
}

/**
 * Warn before closing a tab that is mid-download — the archive would be lost
 * and, on a Blob sink, so would every unsaved part.
 */
function guardUnload(enable: boolean) {
  const handler = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    e.returnValue = '';
  };
  if (enable) window.addEventListener('beforeunload', handler);
  else window.removeEventListener('beforeunload', handler);
  return handler;
}

let unloadHandler: ((e: BeforeUnloadEvent) => void) | null = null;

function setUnloadGuard(active: boolean) {
  if (active && !unloadHandler) {
    unloadHandler = guardUnload(true);
  } else if (!active && unloadHandler) {
    window.removeEventListener('beforeunload', unloadHandler);
    unloadHandler = null;
  }
}

export const useDownloadStore = create<DownloadState>((set, get) => ({
  progress: null,
  active: null,
  handle: null,
  target: null,
  minimized: false,

  start: async (opts) => {
    // Single-flight: a second "Download all" while one is running would double
    // the bandwidth and halve both speeds. Surface the existing panel instead.
    if (get().active) {
      set({ minimized: false });
      return;
    }

    const download = new ZipDownload({
      target: opts.target,
      albumName: opts.albumName,
      scope: opts.scope,
      photoIds: opts.photoIds,
      confirmLarge: opts.confirmLarge,
      onProgress: (p) => set({ progress: p }),
    });

    set({ active: download, handle: download, target: opts.target, minimized: false });
    setUnloadGuard(true);
    try {
      await download.run();
    } finally {
      setUnloadGuard(false);
      set({ active: null });
    }
  },

  cancel: () => {
    get().active?.cancel();
  },

  savePart: async (partIndex) => {
    await get().handle?.savePart(partIndex);
  },

  openPart: (partIndex) => {
    get().handle?.openPart(partIndex);
  },

  saveAll: async () => (await get().handle?.saveAll()) ?? false,

  retrySkipped: async () => {
    // The finished run has already cleared `active`, so rebuild the request
    // from the last progress snapshot plus the album id we stashed at start.
    const snapshot = get().progress;
    const target = get().target;
    if (!snapshot || !target || snapshot.skipped.length === 0) return;
    const photoIds = snapshot.skipped.map((s) => s.photoId);
    set({ progress: null });
    await get().start({
      target,
      albumName: snapshot.albumName,
      scope: 'selection',
      photoIds,
    });
  },

  dismiss: () => {
    // Anything the user never saved is theirs to lose — but don't leave it
    // occupying OPFS quota after they've closed the card.
    void get().handle?.discardStaged();
    set({ progress: null, handle: null, target: null, minimized: false });
  },

  setMinimized: (v) => set({ minimized: v }),
}));
